/**
 * StorefrontGateEditor (TNK-212) — operator UI for the public-store eligibility
 * gate. A single appears only if it matches AT LEAST ONE group; within a group
 * every condition must hold (OR of AND-groups). No groups = everything eligible
 * shows (today's behavior).
 *
 * Example the operator can build here: "commons ≥ $0.20 OR uncommons ≥ $0.50 OR
 * rares ≥ $5" = three groups, each a rarity allow-list + a price minimum.
 *
 * Attribute keys + kinds mirror the server registry (apps/api/src/lib/
 * storefrontGate.ts) — keep them in sync. Suggestion lists are the canonical
 * card vocabularies; the operator can also type a value not listed.
 */
import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";

import type {
  StorefrontGate,
  StorefrontGateCondition,
} from "@/features/storefront";

type GateKind = "enum" | "range" | "overlap";

interface GateAttrMeta {
  key: string;
  label: string;
  kind: GateKind;
  /** Autocomplete hints for enum/overlap attrs; free-text still allowed. */
  suggestions?: string[];
  /** Range unit prefix, e.g. "$". */
  unit?: string;
}

const GATE_ATTRIBUTES: GateAttrMeta[] = [
  {
    key: "rarity",
    label: "Rarity",
    kind: "enum",
    suggestions: [
      "Common",
      "Uncommon",
      "Rare",
      "Promo",
      "Trainer",
      "Energy",
      "Damaged",
      "Sealed",
    ],
  },
  {
    key: "finish",
    label: "Finish",
    kind: "enum",
    suggestions: ["Holo", "Reverse Holo", "Premium"],
  },
  {
    key: "condition",
    label: "Condition",
    kind: "enum",
    suggestions: ["NM", "LP", "MP", "HP", "Damaged"],
  },
  {
    key: "game",
    label: "Game",
    kind: "enum",
    suggestions: ["Pokemon", "Magic", "Yu-Gi-Oh", "One Piece", "Other"],
  },
  {
    key: "tcgSupertype",
    label: "Card type",
    kind: "enum",
    suggestions: ["Pokémon", "Trainer", "Energy"],
  },
  { key: "setName", label: "Set", kind: "enum" },
  { key: "marketPrice", label: "Price", kind: "range", unit: "$" },
  {
    key: "tcgTypes",
    label: "Energy type (Pokémon)",
    kind: "overlap",
    suggestions: [
      "Grass",
      "Fire",
      "Water",
      "Lightning",
      "Psychic",
      "Fighting",
      "Darkness",
      "Metal",
      "Fairy",
      "Dragon",
      "Colorless",
    ],
  },
  {
    key: "mtgColors",
    label: "Color (Magic)",
    kind: "overlap",
    suggestions: ["White", "Blue", "Black", "Red", "Green"],
  },
];

const ATTR_BY_KEY = new Map(GATE_ATTRIBUTES.map((a) => [a.key, a]));

function metaFor(attr: string): GateAttrMeta {
  return ATTR_BY_KEY.get(attr) ?? GATE_ATTRIBUTES[0];
}

/** Small removable-chip value input with autocomplete suggestions. */
function ChipInput({
  values,
  suggestions,
  onChange,
}: {
  values: string[];
  suggestions?: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const listId = `gate-sugg-${suggestions?.[0] ?? "x"}`;
  const add = (raw: string) => {
    const v = raw.trim();
    if (!v || values.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...values, v]);
    setDraft("");
  };
  return (
    <div className="flex flex-1 flex-wrap items-center gap-1">
      {values.map((v) => (
        <span
          key={v}
          className="inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-0.5 text-[11px] text-foreground"
        >
          {v}
          <button
            type="button"
            onClick={() => onChange(values.filter((x) => x !== v))}
            className="text-muted-foreground hover:text-foreground"
            aria-label={`Remove ${v}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        list={listId}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add(draft);
          }
        }}
        onBlur={() => add(draft)}
        placeholder={values.length ? "add…" : "type a value…"}
        className="min-w-[7rem] flex-1 rounded-sm border border-border bg-background px-2 py-1 text-xs"
      />
      {suggestions && suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
    </div>
  );
}

/**
 * Currency input for price thresholds — shows two decimals (e.g. "0.20") so a
 * threshold reads as dollars-and-cents, not a bare "0". Empty = no bound.
 */
function MoneyInput({
  value,
  placeholder,
  onChange,
}: {
  value?: number;
  placeholder: string;
  onChange: (n: number | undefined) => void;
}) {
  const [draft, setDraft] = useState(
    value === undefined ? "" : value.toFixed(2),
  );
  // Resync when the bound value changes externally (e.g. switching attribute
  // resets the condition).
  useEffect(() => {
    setDraft(value === undefined ? "" : value.toFixed(2));
  }, [value]);
  return (
    <input
      inputMode="decimal"
      value={draft}
      placeholder={placeholder}
      onChange={(e) => {
        setDraft(e.target.value);
        const n = parseFloat(e.target.value);
        onChange(e.target.value.trim() === "" || isNaN(n) ? undefined : n);
      }}
      onBlur={() => setDraft(value === undefined ? "" : value.toFixed(2))}
      className="w-24 rounded-sm border border-border bg-background px-2 py-1"
    />
  );
}

/** Clickable multi-select for attributes with a known vocabulary. */
function OptionChips({
  options,
  values,
  onChange,
}: {
  options: string[];
  values: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="flex flex-1 flex-wrap gap-1">
      {options.map((opt) => {
        const on = values.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            aria-pressed={on}
            onClick={() =>
              onChange(on ? values.filter((v) => v !== opt) : [...values, opt])
            }
            className={`rounded-sm border px-2 py-1 text-[11px] transition-colors ${
              on
                ? "border-primary bg-primary/15 text-foreground"
                : "border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function StorefrontGateEditor({
  value,
  onChange,
  storeMinPrice,
}: {
  value: StorefrontGate | null;
  onChange: (next: StorefrontGate | null) => void;
  /** Store-wide minimum price — shown for context; only applies with no gate. */
  storeMinPrice?: number;
}) {
  const groups = value?.groups ?? [];

  // Rebuild immutably; an empty gate collapses to null (= no gating).
  const commit = (nextGroups: typeof groups) =>
    onChange(nextGroups.length ? { groups: nextGroups } : null);

  const patchGroup = (gi: number, conditions: StorefrontGateCondition[]) =>
    commit(groups.map((g, i) => (i === gi ? { conditions } : g)));

  const addGroup = () =>
    commit([...groups, { conditions: [{ attr: "rarity" }] }]);
  const removeGroup = (gi: number) => commit(groups.filter((_, i) => i !== gi));

  const addCondition = (gi: number) =>
    patchGroup(gi, [...groups[gi].conditions, { attr: "rarity" }]);
  const removeCondition = (gi: number, ci: number) =>
    patchGroup(
      gi,
      groups[gi].conditions.filter((_, i) => i !== ci),
    );
  const patchCondition = (
    gi: number,
    ci: number,
    patch: StorefrontGateCondition,
  ) =>
    patchGroup(
      gi,
      groups[gi].conditions.map((c, i) => (i === ci ? patch : c)),
    );

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-medium text-foreground">
          Storefront gate
        </div>
        <p className="text-xs leading-snug text-muted-foreground">
          A card appears in your store only if it matches <strong>any</strong>{" "}
          group below; within a group, <strong>every</strong> condition must
          hold. Leave this empty to show everything that's in stock, priced, and
          pictured.
        </p>
        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
          {groups.length > 0 ? (
            <>
              Pricing: each group&rsquo;s price rule is the floor — the
              store-wide minimum
              {storeMinPrice != null
                ? ` ($${storeMinPrice.toFixed(2)})`
                : ""}{" "}
              does <strong>not</strong> apply while a gate is active. Cards
              priced at $0 never show.
            </>
          ) : (
            <>
              With no gate, only the store-wide minimum
              {storeMinPrice != null
                ? ` ($${storeMinPrice.toFixed(2)})`
                : ""}{" "}
              applies.
            </>
          )}
        </p>
      </div>

      {groups.length === 0 && (
        <p className="rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          No gate — all eligible cards show. Add a group to start gating.
        </p>
      )}

      {groups.map((group, gi) => (
        <div key={gi} className="space-y-2">
          {gi > 0 && (
            <div className="text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              — or —
            </div>
          )}
          <div className="space-y-2 rounded-md border border-border bg-card p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Group {gi + 1} — match all of:
              </span>
              <button
                type="button"
                onClick={() => removeGroup(gi)}
                className="text-xs text-muted-foreground hover:text-destructive"
              >
                Remove group
              </button>
            </div>

            {group.conditions.map((cond, ci) => {
              const meta = metaFor(cond.attr);
              return (
                <div key={ci} className="flex flex-wrap items-center gap-2">
                  <select
                    value={cond.attr}
                    onChange={(e) =>
                      // Switching attribute resets the value (kinds differ).
                      patchCondition(gi, ci, { attr: e.target.value })
                    }
                    className="rounded-sm border border-border bg-background px-2 py-1 text-xs"
                  >
                    {GATE_ATTRIBUTES.map((a) => (
                      <option key={a.key} value={a.key}>
                        {a.label}
                      </option>
                    ))}
                  </select>

                  {meta.kind === "range" ? (
                    <div className="flex flex-1 items-center gap-1 text-xs">
                      <span className="text-muted-foreground">{meta.unit}</span>
                      <MoneyInput
                        value={cond.min}
                        placeholder="min"
                        onChange={(n) =>
                          patchCondition(gi, ci, { ...cond, min: n })
                        }
                      />
                      <span className="text-muted-foreground">to</span>
                      <span className="text-muted-foreground">{meta.unit}</span>
                      <MoneyInput
                        value={cond.max}
                        placeholder="max"
                        onChange={(n) =>
                          patchCondition(gi, ci, { ...cond, max: n })
                        }
                      />
                    </div>
                  ) : meta.suggestions && meta.suggestions.length > 0 ? (
                    <OptionChips
                      options={meta.suggestions}
                      values={cond.values ?? []}
                      onChange={(vals) =>
                        patchCondition(gi, ci, {
                          attr: cond.attr,
                          values: vals,
                        })
                      }
                    />
                  ) : (
                    <ChipInput
                      values={cond.values ?? []}
                      onChange={(vals) =>
                        patchCondition(gi, ci, {
                          attr: cond.attr,
                          values: vals,
                        })
                      }
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => removeCondition(gi, ci)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Remove condition"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => addCondition(gi)}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Plus className="h-3 w-3" /> Add condition
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addGroup}
        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
      >
        <Plus className="h-3.5 w-3.5" /> Add rule group
      </button>
    </div>
  );
}
